import api from './api';
import { WineColor } from './wineNameService';

/**
 * P5c — the operator console's API surface (REVIEW-AND-VERIFICATION-DESIGN
 * §7–§9). Server routes are feature-gated (OPERATOR_CONSOLE_ENABLED): when the
 * console is dark the queue returns a plain 404, which the page shows as
 * "not enabled on this environment".
 *
 * The detail payload is the scan document verbatim — the API owns truth, this
 * screen owns presentation. Types below describe the parts the screen renders
 * and leave the rest as unknown, mirroring the server's own open objects.
 */

const BASE = '/admin/label-scan-review';

// ── Queue ─────────────────────────────────────────────────────────────────────

export interface ReviewQueueRow {
  _id: string;
  venueId: string;
  status: string;
  createdAt: string;
  verdict: string | null;
  producerText: string | null;
  appellationText: string | null;
  vintage: number | null;
  candidates: number;
  verification: string | null;
  flags: number;
  /** P5d throughput: presigned front-image thumbnail (null when unavailable). */
  thumbUrl: string | null;
  /** P5d throughput: the live claimant's email, or null. */
  claimedBy: string | null;
}

/**
 * §5a — the operational summary, carried on the queue response. This was
 * a daily email; it is a banner now, because the only reviewer is already
 * looking at this screen. Computed globally, so a status filter narrows
 * the rows without shrinking the truth about the queue.
 */
export interface QueueSummary {
  since: string;
  pending: { total: number; byStatus: Record<string, number>; oldestDays: number | null };
  decided: Record<string, number>;
  taughtAliases: number;
  refusedAliases: number;
  contradictions: Array<{ scanId: string; producerText: string | null; matchedProbe: boolean }>;
  spendUsd: number;
  /**
   * GK-1 §4.1 — grape findings live in the banner's counts. Optional: an
   * API deployed before GK-1 simply omits it.
   */
  grape?: { contradictions: number; coverage: number; precision: number };
}

export interface ReviewQueueResponse {
  data: ReviewQueueRow[];
  count: number;
  total: number;
  limit: number;
  summary?: QueueSummary;
}

// ── Detail (the scan document, presigned) ─────────────────────────────────────

export interface ScanImage {
  s3Key: string;
  side: 'front' | 'back';
  mimeType: string;
  sizeBytes: number;
  sha256?: string;
  originalName?: string;
  url: string;
  urlExpiresInSeconds: number;
}

/** One read slot as the vision artifact emits it. */
export interface VisionSlot {
  text?: string | null;
  value?: number | null;
  values?: string[];
  confidence?: number;
  authority?: string;      // 'structural' | 'positional' | 'typographic' | 'guess' | 'hierarchy-candidate'
  evidence?: string;
  vintageType?: string;
}

export interface SlotAssignment {
  guessedSlots?: string[];
  unconfirmedSlots?: string[];
  cappedSlots?: string[];
  capApplied?: boolean;
  verdictBeforeCap?: string;
  regionDiscriminated?: boolean;
  discriminatorCapped?: boolean;
  unsupportedByTranscription?: string[];
  thinMatchDemoted?: boolean;
}

export interface CandidateEvidence {
  type: 'exact' | 'alias' | 'fold' | 'titlestrip' | 'fuzzy' | 'exclusion' | 'flag';
  detail: string;
}

export interface ResolutionCandidate {
  identityRef: { producerId?: string; regionId?: string; grapeIds?: string[]; wineNameId?: string };
  display: string;
  confidence: { overall: number; components: Record<string, number> };
  matchDetail: Record<string, string>;
  evidence: CandidateEvidence[];
  disambiguation?: { fullName?: string | null; country?: string | null };
}

export interface ResolutionFlag {
  code: string;
  detail: string;
  userMessage?: string;
}

export interface VerificationSource { url: string; title?: string; quote?: string }

export interface VerificationClaim {
  slot: string;
  labelValue: string;
  finding: 'exists' | 'not_found' | 'variant' | 'contradiction' | string;
  canonicalSpelling?: string | null;
  note?: string;
  sources?: VerificationSource[];
  /** §13.6 trust tier of the claim's best source (tech-sheet claims carry it). */
  sourceTier?: 'tech-sheet' | 'producer-site' | 'trade' | 'merchant';
}

export interface SheetUse {
  techSheetId: string | null;
  s3Key: string | null;
  sourceUrl: string;
  sourceTier: 'tech-sheet' | 'producer-site' | 'trade' | 'merchant';
  statedVintage: number | null;
  vintageMatch: boolean;
  statement: string;
  claims: Array<{ field: string; scope: 'cuvee' | 'vintage'; value: unknown; quote: string }>;
  withheld: string[];
  reused: boolean;
  /** Presigned by the detail endpoint — view the sheet like a photo. */
  url?: string;
}

export interface WebVerification {
  artifact?: string;
  model?: string;
  status: 'verified' | 'unavailable' | 'unverified' | string;
  reason?: string;
  claims?: VerificationClaim[];
  usage?: { searches?: number; inputTokens?: number; outputTokens?: number };
  costUsd?: number;
  sheet?: SheetUse;
}

export interface ScanReview {
  action: string;
  actor?: { id?: string | null; email?: string | null };
  at?: string;
  reason?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  cascade?: Record<string, unknown>;
}

export interface ScanDetail {
  _id: string;
  venueId: string;
  status: string;
  createdAt: string;
  images: ScanImage[];
  visionRead?: Record<string, VisionSlot>;
  resolution?: {
    verdict?: string;
    candidates?: ResolutionCandidate[];
    flags?: ResolutionFlag[];
    slotAssignment?: SlotAssignment;
    visionFlags?: string[];
    parsed?: Record<string, unknown>;
    webEvidence?: Array<{ type: string; slot: string; detail: string; url?: string }>;
    engineVersion?: string;
    costUsd?: number | null;
  };
  webVerification?: WebVerification;
  review?: ScanReview;
  rescanRequested?: { actor?: { email?: string }; at?: string; note?: string | null };
  costUsd?: number;
  error?: string;
  /**
   * P5b §5 — staff corrections to the READING, informing the operator.
   * The coupling designed at P5c: absent means the section renders
   * nothing, which is still true for every pre-P5b scan.
   */
  corrections?: Array<{
    slot: string;
    from: string | number | null;
    to: string | number | null;
    kind: 'edit' | 'pick_candidate' | 'vintage' | 'photo_added' | string;
    actorEmail: string | null;
    at: string;
  }>;
  /** P5b §4 — what the staff member committed from this scan. */
  staffAcceptance?: {
    at: string;
    actor: { email: string | null };
    wineNameId: string;
    wineId: string;
    inventoryItemId: string;
    converged: boolean;
    provisional: boolean;
  };
}

// ── Decisions ─────────────────────────────────────────────────────────────────

export interface ApproveNewFields {
  producerId?: string;
  newProducer?: { name: string; country: string };
  regionId?: string;
  grapeIds?: string[];
  specialName?: string;
  /** Required: the reading never carries color — the operator states it. */
  color: WineColor;
  type?: string;
  subType?: string;
  /** The familiar flow ends with a WINE — optional so NV/unreadable still lands. */
  vintage?: { vintageType: string; vintage?: number };
}

export interface FindOrCreateResult {
  created: boolean;
  kind: 'producer' | 'region' | 'grape';
  entity: any;
}

export interface MergeCandidate {
  _id: string;
  displayName: string;
  similarity: number;
}

export interface SlotProbeResult {
  artifact: string;
  slot: string;
  name: string;
  finding: 'known' | 'unknown' | 'ambiguous';
  summary: string | null;
  canonicalName: string | null;
  country: string | null;
  parent: string | null;
  sources: Array<{ url: string; title?: string; quote?: string }>;
  model: string;
  usage: { searches: number; inputTokens: number; outputTokens: number };
  costUsd: number | null;
}

export type DecisionRequest =
  | { action: 'approve_new'; fields: ApproveNewFields }
  | { action: 'merge'; wineNameId: string; update?: { specialName?: string } }
  | { action: 'reject'; reason: string }
  | { action: 'rescan'; note?: string };

export interface DecisionResponse {
  decided: boolean;
  action: string;
  wineNameId?: string;
  displayName?: string;
  mintedProducer?: boolean;
  applied?: Record<string, unknown>;
  cascade?: Record<string, unknown>;
  note?: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

const labelScanReviewService = {
  getQueue: async (params?: { status?: string; venueId?: string; limit?: number }): Promise<ReviewQueueResponse> => {
    const response = await api.get(`${BASE}/queue`, { params });
    return response.data;
  },

  getScan: async (id: string): Promise<ScanDetail> => {
    const response = await api.get(`${BASE}/${id}`);
    return response.data;
  },

  decide: async (id: string, body: DecisionRequest): Promise<DecisionResponse> => {
    const response = await api.post(`${BASE}/${id}/decision`, body);
    return response.data;
  },

  /**
   * The pickers' guarded mint. A FOLD duplicate answers created:false with
   * the surviving entity, which the picker preselects instead of minting.
   * A STRIP match ('Bellavista' vs 'Bodegas Bellavista') is not a duplicate
   * and not a refusal — it throws 409 PRODUCER_CHOICE_REQUIRED so the caller
   * can put the question to the operator and retry with
   * confirmDistinctProducer.
   */
  findOrCreate: async (body: { kind: 'producer' | 'region' | 'grape'; name: string; country?: string; grapeColor?: string; parentRegionId?: string; scanId?: string; confirmDistinctProducer?: boolean }): Promise<FindOrCreateResult> => {
    const response = await api.post(`${BASE}/find-or-create`, body);
    return response.data;
  },

  /** Winenames ranked by similarity to the label reading (or to q). */
  mergeCandidates: async (id: string, q?: string): Promise<{ data: MergeCandidate[]; hint: string | null }> => {
    const response = await api.get(`${BASE}/${id}/merge-candidates`, { params: q ? { q } : {} });
    return response.data;
  },

  /**
   * Ask the web a targeted question about one unresolved slot value — one
   * Haiku call, on demand, recorded on the scan with its own cost. The
   * answer proposes and evidences; only the operator's Create creates.
   */
  slotProbe: async (id: string, body: { slot: 'region' | 'producer' | 'grape'; name: string }): Promise<SlotProbeResult> => {
    const response = await api.post(`${BASE}/${id}/slot-probe`, body);
    return response.data;
  },

  /** P5d: claim-on-open. 409 CLAIMED means someone else is reviewing. */
  claim: async (id: string): Promise<{ claimed: boolean; claim: { actor: { email: string }; expiresAt: string } }> => {
    const response = await api.post(`${BASE}/${id}/claim`);
    return response.data;
  },
};

export default labelScanReviewService;
